import { AbstractControl, ValidationErrors } from '@angular/forms';
import dayjs from 'dayjs';

export function timezoneOffset(timezone: string): string {
  const part = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'longOffset' })
    .formatToParts(dayjs().toDate())
    .find((item) => item.type === 'timeZoneName');
  return part?.value ?? '';
}

export function timezoneLabel(timezone: string): string {
  const offset = timezoneOffset(timezone);
  return offset ? `${timezone} (${offset})` : timezone;
}

export const TIMEZONE_OPTIONS = ['UTC', ...Intl.supportedValuesOf('timeZone').filter((tz) => tz !== 'UTC')].map(
  (value) => ({ value, label: timezoneLabel(value) }),
);

export function timezoneValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) return null;
  try {
    new Intl.DateTimeFormat('en', { timeZone: control.value });
    return null;
  } catch (error) {
    if (error instanceof RangeError) return { timezone: true };
    throw error;
  }
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function timezoneOffsetMs(utcMs: number, timezone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(utcMs));
  const value = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  const localAsUtc = Date.UTC(
    value('year'),
    value('month') - 1,
    value('day'),
    value('hour'),
    value('minute'),
    value('second'),
  );
  return localAsUtc - Math.floor(utcMs / 1000) * 1000;
}

/**
 * UTC instant (ms) of 00:00 on a local `YYYY-MM-DD` date. Resolved from the real zone offsets because
 * `dayjs.tz` returns the wrong start for the day a daylight-saving change happens.
 */
export function zonedDayStartMs(date: string, timezone: string): number {
  const [year, month, day] = date.split('-').map(Number);
  const local = Date.UTC(year, month - 1, day);
  const firstGuess = local - timezoneOffsetMs(local, timezone);
  return local - timezoneOffsetMs(firstGuess, timezone);
}

/** UTC instant (ms) of 00:00 on the local day after `date` (exclusive end of that day). */
export function zonedNextDayStartMs(date: string, timezone: string): number {
  const [year, month, day] = date.split('-').map(Number);
  const nextDate = new Date(Date.UTC(year, month - 1, day) + MS_PER_DAY).toISOString().slice(0, 10);
  return zonedDayStartMs(nextDate, timezone);
}
